import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import RoleplaySimulator from './pages/RoleplaySimulator';
import ScriptGenerator from './pages/ScriptGenerator';
import DailyChallenges from './pages/DailyChallenges';
import Profile from './pages/Profile';
import PaymentSuccess from './pages/PaymentSuccess';
import Leaderboard from './pages/Leaderboard';
import CommunityFeed from './pages/CommunityFeed';
import Analytics from './pages/Analytics';
import ReferralProgram from './pages/ReferralProgram';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { API } from './lib/api';
import { Toaster } from './components/ui/sonner';
import { initGA, trackPageView } from './lib/analytics';

function AppRouter() {
  const location = useLocation();
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/roleplay" element={<ProtectedRoute><RoleplaySimulator /></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute><ScriptGenerator /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><DailyChallenges /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><CommunityFeed /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><ReferralProgram /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
    </Routes>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      checkOnboarding(location.state.user.user_id);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await API.get('/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
        await checkOnboarding(response.data.user_id);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state]);

  const checkOnboarding = async (userId) => {
    try {
      const response = await API.get('/onboarding/status');
      setHasCompletedOnboarding(response.data.completed);
      
      // If user hasn't completed onboarding and is not on onboarding page, redirect
      if (!response.data.completed && location.pathname !== '/onboarding') {
        navigate('/onboarding', { replace: true });
      }
      // If user has completed onboarding and is on onboarding page, redirect to dashboard
      else if (response.data.completed && location.pathname === '/onboarding') {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      // If endpoint doesn't exist or error, assume not completed
      setHasCompletedOnboarding(false);
    }
  };

  if (isAuthenticated === null || hasCompletedOnboarding === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics on app load
    initGA();
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" theme="dark" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;